import React, { useRef, useState, useEffect } from 'react';
import { Box, Grid, GridItem, Text, Spinner, Center, VStack, HStack, Badge } from '@chakra-ui/react';
import EventCard from './EventCard';

const CalendarView = ({ events, view, selectedDate, onTimeSlotClick, onEventClick, loading }) => {
  const gridRef = useRef(null);
  
  // Scroll to current time on mount for week/day views
  useEffect(() => {
    if ((view === 'week' || view === 'day') && gridRef.current) {
      const currentHour = new Date().getHours();
      const scrollPosition = (currentHour - 1) * 60; // 60px per hour
      gridRef.current.scrollTop = scrollPosition;
    }
  }, [view]);

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="army.500" />
      </Center>
    );
  }

  // Helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add previous month's trailing days
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Add next month's leading days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      return (eventStart <= dayEnd && eventEnd >= dayStart);
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleDayClick = (date, e) => {
    if (view === 'month') {
      // For month view, open the event modal directly
      onTimeSlotClick(date, null);
    }
  };

  const handleTimeSlotClick = (date, hour, e) => {
    e.stopPropagation();
    
    if (view === 'week' || view === 'day') {
      const clickedTime = `${hour.toString().padStart(2, '0')}:00`;
      // Open the event modal directly
      onTimeSlotClick(date, clickedTime);
    }
  };

  // Get current time for time indicator
  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    return (hours * 60 + minutes) / (24 * 60) * 100;
  };

  // Render month view with improved grid
  const renderMonthView = () => {
    const days = getDaysInMonth(selectedDate);
    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    return (
      <Box borderRadius="lg" overflow="hidden" boxShadow="sm">
        {/* Week days header */}
        <Grid 
          templateColumns="repeat(7, 1fr)" 
          bg="green.600"
          position="sticky"
          top={0}
          zIndex={10}
        >
          {weekDays.map(day => (
            <GridItem
              key={day}
              p={3}
              textAlign="center"
              color="white"
              fontWeight="semibold"
              fontSize="sm"
              borderRightWidth={day !== 'Sáb' ? '1px' : '0'}
              borderRightColor="whiteAlpha.300"
            >
              {day}
            </GridItem>
          ))}
        </Grid>
        
        {/* Calendar days grid */}
        <Grid 
          templateColumns="repeat(7, 1fr)"
          gap={0}
          bg="gray.50"
        >
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day.date);
            const isCurrentMonth = day.isCurrentMonth;
            const today = isToday(day.date);
            
            return (
              <GridItem
                key={index}
                minH="120px"
                bg={!isCurrentMonth ? 'gray.100' : today ? 'green.50' : 'white'}
                borderWidth="1px"
                borderColor="gray.200"
                cursor="pointer"
                position="relative"
                transition="all 0.2s"
                onClick={(e) => handleDayClick(day.date, e)}
                _hover={{ 
                  bg: isCurrentMonth ? 'gray.50' : 'gray.150',
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                  zIndex: 1
                }}
              >
                <VStack align="stretch" h="full" spacing={0}>
                  {/* Date header */}
                  <HStack justify="space-between" p={2} pb={1}>
                    <Text
                      fontSize="sm"
                      fontWeight={today ? 'bold' : 'medium'}
                      color={!isCurrentMonth ? 'gray.400' : today ? 'green.600' : 'gray.700'}
                    >
                      {day.date.getDate()}
                    </Text>
                    {today && (
                      <Badge colorScheme="green" size="sm">
                        Hoje
                      </Badge>
                    )}
                  </HStack>
                  
                  {/* Events area */}
                  <VStack align="stretch" px={2} pb={2} spacing={1} flex={1}>
                    {dayEvents.length > 0 ? (
                      <>
                        {/* Show event dots for visual indication */}
                        <HStack spacing={1} mb={1}>
                          {dayEvents.slice(0, 5).map((event, i) => (
                            <Box
                              key={i}
                              w={2}
                              h={2}
                              borderRadius="full"
                              bg={getEventColorDot(event.type)}
                            />
                          ))}
                          {dayEvents.length > 5 && (
                            <Text fontSize="xs" color="gray.500">
                              +{dayEvents.length - 5}
                            </Text>
                          )}
                        </HStack>
                        
                        {/* Show first 2 events */}
                        {dayEvents.slice(0, 2).map((event) => (
                          <Box
                            key={event._id}
                            p={1}
                            bg={event.color ? `${event.color}20` : `${getEventColorScheme(event.type)}.100`}
                            borderRadius="sm"
                            fontSize="xs"
                            color={event.color ? getContrastColor(event.color) : `${getEventColorScheme(event.type)}.700`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                            _hover={{ bg: event.color ? `${event.color}30` : `${getEventColorScheme(event.type)}.200` }}
                            cursor="pointer"
                            isTruncated
                          >
                            <Text isTruncated>
                              {!event.allDay && formatTime(event.startDate)} {event.title}
                            </Text>
                          </Box>
                        ))}
                        
                        {dayEvents.length > 2 && (
                          <Text 
                            fontSize="xs" 
                            color="gray.500" 
                            cursor="pointer"
                            _hover={{ color: 'gray.700' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Could open a day view or list modal here
                            }}
                          >
                            +{dayEvents.length - 2} mais
                          </Text>
                        )}
                      </>
                    ) : (
                      <Box flex={1} />
                    )}
                  </VStack>
                </VStack>
              </GridItem>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Helper function to determine text color based on background
  const getContrastColor = (hexColor) => {
    // If no custom color, return white (for default colored backgrounds)
    if (!hexColor) return 'white';
    
    // Convert hex to RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light backgrounds, white for dark
    return luminance > 0.5 ? 'gray.800' : 'white';
  };

  // Helper functions for event colors
  const getEventColorScheme = (type) => {
    switch (type) {
      case 'appointment': return 'blue';
      case 'meeting': return 'purple';
      case 'task': return 'green';
      case 'reminder': return 'orange';
      default: return 'gray';
    }
  };

  const getEventColorDot = (type) => {
    switch (type) {
      case 'appointment': return 'blue.500';
      case 'meeting': return 'purple.500';
      case 'task': return 'green.500';
      case 'reminder': return 'orange.500';
      default: return 'gray.500';
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render week view with improved grid
  const renderWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const currentTimePos = getCurrentTimePosition();
    
    return (
      <Box 
        ref={gridRef} 
        position="relative" 
        overflowY="auto" 
        maxH="600px"
        borderRadius="lg"
        boxShadow="sm"
        bg="white"
      >
        {/* Header with days */}
        <Grid 
          templateColumns="80px repeat(7, 1fr)" 
          position="sticky"
          top={0}
          zIndex={20}
          bg="white"
          borderBottomWidth="2px"
          borderBottomColor="gray.200"
        >
          <GridItem bg="gray.50" />
          {weekDays.map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <GridItem
                key={index}
                bg={isToday ? "green.500" : "green.600"}
                color="white"
                p={3}
                textAlign="center"
                borderRightWidth={index < 6 ? "1px" : "0"}
                borderRightColor="whiteAlpha.300"
              >
                <Text fontWeight="semibold" fontSize="sm" color="white">
                  {day.toLocaleDateString('pt-BR', { weekday: 'short' })}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="white">
                  {day.getDate()}
                </Text>
              </GridItem>
            );
          })}
        </Grid>
        
        {/* Time grid with improved styling */}
        <Box position="relative">
          {/* Current time indicator */}
          {view === 'week' && (
            <Box
              position="absolute"
              top={`${currentTimePos}%`}
              left="80px"
              right={0}
              h="2px"
              bg="red.500"
              zIndex={15}
              pointerEvents="none"
            >
              <Box
                position="absolute"
                left="-6px"
                top="-4px"
                w="10px"
                h="10px"
                bg="red.500"
                borderRadius="full"
              />
            </Box>
          )}
          
          <Grid templateColumns="80px repeat(7, 1fr)">
            {hours.map(hour => (
              <React.Fragment key={hour}>
                {/* Time label */}
                <GridItem
                  p={2}
                  fontSize="xs"
                  color="gray.600"
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  fontWeight="medium"
                >
                  {`${hour.toString().padStart(2, '0')}:00`}
                </GridItem>
                
                {/* Day columns */}
                {weekDays.map((day, dayIndex) => {
                  const isCurrentHour = 
                    new Date().getHours() === hour && 
                    day.toDateString() === new Date().toDateString();
                  
                  return (
                    <GridItem
                      key={`${hour}-${dayIndex}`}
                      h="60px"
                      borderBottomWidth="1px"
                      borderRightWidth={dayIndex < 6 ? "1px" : "0"}
                      borderColor="gray.200"
                      bg={isCurrentHour ? "blue.50" : "white"}
                      p={1}
                      cursor="pointer"
                      position="relative"
                      transition="all 0.2s"
                      onClick={(e) => handleTimeSlotClick(day, hour, e)}
                      _hover={{ 
                        bg: isCurrentHour ? "blue.100" : "gray.50",
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          border: '2px solid',
                          borderColor: 'green.400',
                          borderRadius: 'sm',
                          pointerEvents: 'none'
                        }
                      }}
                    >
                      {/* Events are rendered in a separate layer */}
                    </GridItem>
                  );
                })}
              </React.Fragment>
            ))}
          </Grid>
          
          {/* Events layer - renders events as absolute positioned blocks */}
          <Box position="absolute" top={0} left="80px" right={0} bottom={0} pointerEvents="none">
            <Grid templateColumns="repeat(7, 1fr)" h="full">
              {weekDays.map((day, dayIndex) => {
                const dayEvents = getEventsForDayColumn(day);
                
                return (
                  <GridItem key={dayIndex} position="relative">
                    {dayEvents.map((event) => {
                      const { top, height } = calculateEventPosition(event, day);
                      
                      return (
                        <Box
                          key={event._id}
                          position="absolute"
                          top={`${top}px`}
                          left="2px"
                          right="2px"
                          height={`${height}px`}
                          bg={event.color || `${getEventColorScheme(event.type)}.500`}
                          color={getContrastColor(event.color)}
                          fontSize="xs"
                          borderRadius="sm"
                          cursor="pointer"
                          p={1}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          _hover={{ 
                            bg: event.color ? event.color : `${getEventColorScheme(event.type)}.600`,
                    filter: event.color ? 'brightness(0.9)' : undefined,
                            transform: 'scale(1.02)',
                            zIndex: 20
                          }}
                          transition="all 0.2s"
                          zIndex={10}
                          boxShadow="sm"
                          overflow="hidden"
                          pointerEvents="auto"
                        >
                          <Text fontWeight="bold" fontSize="xs" color={getContrastColor(event.color)}>
                            {formatTime(event.startDate)} - {formatTime(event.endDate)}
                          </Text>
                          <Text isTruncated fontSize="xs" color={getContrastColor(event.color)}>
                            {event.title}
                          </Text>
                        </Box>
                      );
                    })}
                  </GridItem>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render day view
  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <Box ref={gridRef} position="relative" overflowY="auto" maxH="600px">
        <Grid templateColumns="80px 1fr" gap={0}>
          {/* Header */}
          <Box />
          <Box
            bg="army.500"
            color="white"
            p={3}
            textAlign="center"
            fontWeight="bold"
            position="sticky"
            top={0}
            zIndex={10}
          >
            {selectedDate.toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </Box>
          
          {/* Time slots */}
          {hours.map(hour => {
            const hourEvents = getEventsForHour(selectedDate, hour);
            return (
              <React.Fragment key={hour}>
                <Box
                  p={2}
                  fontSize="sm"
                  color="gray.500"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                >
                  {`${hour.toString().padStart(2, '0')}:00`}
                </Box>
                <Box
                  h="60px"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  p={2}
                  cursor="pointer"
                  onClick={(e) => handleTimeSlotClick(selectedDate, hour, e)}
                  _hover={{ bg: 'gray.50' }}
                  position="relative"
                >
                  {/* Events are rendered in a separate layer */}
                </Box>
              </React.Fragment>
            );
          })}
        </Grid>
        
        {/* Events layer for day view */}
        <Box position="absolute" top="48px" left="80px" right={0} bottom={0} pointerEvents="none">
          {(() => {
            const dayEvents = getEventsForDayColumn(selectedDate);
            
            return dayEvents.map((event) => {
              const { top, height } = calculateEventPosition(event, selectedDate);
              
              return (
                <Box
                  key={event._id}
                  position="absolute"
                  top={`${top}px`}
                  left="8px"
                  right="8px"
                  height={`${height}px`}
                  bg={event.color || `${getEventColorScheme(event.type)}.500`}
                  color={getContrastColor(event.color)}
                  fontSize="sm"
                  borderRadius="md"
                  cursor="pointer"
                  p={2}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                  _hover={{ 
                    bg: event.color ? event.color : `${getEventColorScheme(event.type)}.600`,
                    filter: event.color ? 'brightness(0.9)' : undefined,
                    transform: 'scale(1.02)',
                    zIndex: 20
                  }}
                  transition="all 0.2s"
                  zIndex={10}
                  boxShadow="md"
                  overflow="hidden"
                  pointerEvents="auto"
                >
                  <HStack spacing={2} align="center">
                    <Text fontWeight="bold" fontSize="sm" flexShrink={0} color={getContrastColor(event.color)}>
                      {formatTime(event.startDate)} - {formatTime(event.endDate)}
                    </Text>
                    <Text color={getContrastColor(event.color)} opacity={0.7}>•</Text>
                    <Text fontWeight="medium" fontSize="md" isTruncated color={getContrastColor(event.color)}>
                      {event.title}
                    </Text>
                    
                    {event.participants && event.participants.length > 0 && (
                      <>
                        <Text color={getContrastColor(event.color)} opacity={0.7}>•</Text>
                        <HStack spacing={1}>
                          <Text fontSize="sm" color={getContrastColor(event.color)} opacity={0.9}>
                            👥
                          </Text>
                          <Text fontSize="sm" color={getContrastColor(event.color)} opacity={0.9}>
                            {event.participants.map(p => {
                              const fullName = p.nomeCompleto || p.name || p.email || 'Participante';
                              return fullName.split(' ')[0];
                            }).join(', ')}
                          </Text>
                        </HStack>
                      </>
                    )}
                    
                    {event.location && (
                      <>
                        <Text color={getContrastColor(event.color)} opacity={0.7}>•</Text>
                        <Text fontSize="sm" color={getContrastColor(event.color)} opacity={0.9} isTruncated maxW="200px">
                          📍 {event.location}
                        </Text>
                      </>
                    )}
                  </HStack>
                  {height > 60 && event.description && (
                    <Text fontSize="sm" color={getContrastColor(event.color)} opacity={0.9} mt={1} isTruncated>
                      {event.description}
                    </Text>
                  )}
                </Box>
              );
            });
          })()}
        </Box>
      </Box>
    );
  };

  const getEventsForHour = (date, hour) => {
    return events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const hourStart = new Date(date);
      hourStart.setHours(hour, 0, 0, 0);
      const hourEnd = new Date(date);
      hourEnd.setHours(hour, 59, 59, 999);
      
      return (eventStart <= hourEnd && eventEnd >= hourStart);
    });
  };

  // Get events for a specific day in week/day view
  const getEventsForDayColumn = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      // Check if event occurs on this day
      return (eventStart <= dayEnd && eventEnd >= dayStart);
    });
  };

  // Calculate event position and height for week/day view
  const calculateEventPosition = (event, date) => {
    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    // Adjust times to be within the current day
    const displayStart = eventStart < dayStart ? dayStart : eventStart;
    const displayEnd = eventEnd > dayEnd ? dayEnd : eventEnd;

    // Calculate position (top) based on start time
    // Each hour row is 60px tall
    const startHour = displayStart.getHours();
    const startMinutes = displayStart.getMinutes();
    const top = startHour * 60 + startMinutes; // 60px per hour + 1px per minute

    // Calculate height based on duration
    const durationMs = displayEnd - displayStart;
    const durationMinutes = durationMs / (1000 * 60);
    const height = Math.max(20, durationMinutes); // 1px per minute, minimum 20px

    return { top, height };
  };

  // Render based on view
  if (view === 'month') {
    return renderMonthView();
  } else if (view === 'week') {
    return renderWeekView();
  } else if (view === 'day') {
    return renderDayView();
  }
};

export default CalendarView;